import React, { useState, useEffect, useCallback } from 'react';
import './Health.css';

const Health = ({ teamcityApi, onBack }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    if (!teamcityApi) return;

    try {
      const data = await teamcityApi.getAgentHealth();
      const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
      setAgents(sorted);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamcityApi]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getShortAgentName = (fullName) => {
    const parts = fullName.split(/[\s\-_]+/);
    return parts[parts.length - 1] || fullName;
  };

  return (
    <div className="health-container">
      <div className="health-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h2>Build Server Health</h2>
        <div className="health-actions">
          {lastRefresh && (
            <span className="last-refresh">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          Error fetching agent health: {error}
        </div>
      )}

      {loading && agents.length === 0 ? (
        <div className="status-message checking">
          <p>Loading agent health data...</p>
        </div>
      ) : agents.length === 0 ? (
        <div className="empty-state">
          <p>No build agents found.</p>
        </div>
      ) : (
        <div className="health-section">
          <h3>Disk Space</h3>
          <div className="health-agents-list">
            {agents.map(agent => {
              const maxSpaceGb = agent.maxSpaceGb;
              const maxSpaceMb = maxSpaceGb * 1024;
              const freeSpaceMb = agent.freeSpaceMb;
              const freeSpaceGb = freeSpaceMb !== null ? freeSpaceMb / 1024 : null;
              const usedMb = freeSpaceMb !== null ? Math.max(0, maxSpaceMb - freeSpaceMb) : 0;
              const usedPercent = freeSpaceMb !== null ? Math.min((usedMb / maxSpaceMb) * 100, 100) : 0;
              const isDisconnected = !agent.connected;

              return (
                <div
                  key={agent.id}
                  className={`health-agent-row ${isDisconnected ? 'disconnected' : ''}`}
                >
                  <a
                    href={`https://azragames.teamcity.com/agentDetails.html?id=${agent.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="agent-name-link"
                  >
                    <div className="agent-name">{getShortAgentName(agent.name)}</div>
                  </a>
                  <div className="health-agent-info">
                    <div className="progress-bar">
                      <div
                        className={`progress-fill ${usedPercent > 90 ? 'progress-fill-failure' : usedPercent > 70 ? 'progress-fill-warning' : ''}`}
                        style={{ width: `${usedPercent}%` }}
                      />
                    </div>
                    <div className="health-space-info">
                      <span>Available: {freeSpaceGb !== null ? `${freeSpaceGb.toFixed(1)} GB` : 'N/A'}</span>
                      <span>Total: {maxSpaceGb.toFixed(1)} GB</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Health;
